'use client'

import { memo, useCallback, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { Plus } from 'lucide-react'
import { cn } from '@/lib/utils'
import { NinxlScrollReveal } from './ninxl-scroll-reveal'
import { NinxlSectionBackdrop, NinxlSectionHeader } from './ninxl-graphics'

const FAQ_ITEMS = [
  {
    question: 'What is NINX?',
    answer:
      'NINX is the private desktop agent. It runs locally on your Mac or Windows machine with Gmail, Calendar, brokers, Notion, automations and more, while keeping your data and inference on your computer by default.',
  },
  {
    question: 'Is it secure?',
    answer:
      'Yes. CASA certified, and our SOC 2 Type II audit is in progress. On-device chats and tools stay on your system. If you use cloud credits, providers run under a Zero Data Retention (ZDR) policy. You explicitly approve every tool call.',
  },
  {
    question: 'How is NINX different from Codex?',
    answer:
      'NINX is a broad private desktop agent for email, automations, research and daily tasks. Codex is specialized for interactive coding, file edits and development workflows.',
  },
  {
    question: 'Does it work with mobile and web?',
    answer:
      'Yes. One account. Start on your phone, continue on NINX, or jump into the browser at t.nin.in. Everything stays in sync.',
  },
  {
    question: 'Does NINX have voice?',
    answer:
      'Yes. NINX includes an on-device voice mode: speak to your agent with local speech-to-text, the same chat and tool loop, and on-device text-to-speech. It works offline with local models, supports barge-in while the assistant is speaking, and keeps voice turns in your chat history.',
  },
  {
    question: 'What is InkFlow?',
    answer:
      'InkFlow is NINX\'s system-wide writing layer for macOS. Dictate to transcribe speech into any app, Instruct to generate or rewrite text from a spoken command, or Transform by chaining dictation with a follow-up instruction. Whisper runs on-device; optional AI polish uses your active local or BYOK model.',
  },
] as const

export const NinxlFaqSection = memo(function NinxlFaqSection() {
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null)

  const toggleFaq = useCallback((index: number) => {
    setOpenFaqIndex((prev) => (prev === index ? null : index))
  }, [])

  return (
    <section className="relative overflow-hidden py-20 bg-[#F8FAFC]">
      <NinxlSectionBackdrop variant="faq" position="top" />
      <div className="mx-auto max-w-3xl px-6">
        <NinxlScrollReveal className="mb-9">
          <NinxlSectionHeader label="FAQ" title="Common questions" className="mb-0 [&_h2]:text-3xl" />
        </NinxlScrollReveal>

        <div className="ninxl-faq-shell rounded-3xl border border-slate-200 bg-white shadow-lg shadow-slate-200/40 overflow-hidden">
          {FAQ_ITEMS.map((item, i) => {
            const isOpen = openFaqIndex === i
            return (
              <motion.div
                key={item.question}
                className={cn('border-b border-slate-100 last:border-b-0', isOpen && 'bg-[#f6f9fd]/60')}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06, duration: 0.45 }}
              >
                <button
                  type="button"
                  onClick={() => toggleFaq(i)}
                  className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left text-sm font-medium group"
                >
                  <span className="flex items-center gap-3">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#21728d]/10 text-[10px] font-bold text-[#21728d]">
                      {i + 1}
                    </span>
                    <span className={cn(isOpen ? 'text-[#21728d]' : 'text-slate-800 group-hover:text-slate-950')}>
                      {item.question}
                    </span>
                  </span>
                  <motion.span animate={{ rotate: isOpen ? 45 : 0 }} transition={{ duration: 0.25 }}>
                    <Plus className={cn('h-4 w-4 shrink-0 text-slate-400', isOpen && 'text-[#21728d]')} />
                  </motion.span>
                </button>
                <AnimatePresence initial={false}>
                  {isOpen ? (
                    <motion.div
                      key="content"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                    >
                      <div className="px-6 pb-6 pl-[3.25rem] text-sm text-slate-600 leading-relaxed">{item.answer}</div>
                    </motion.div>
                  ) : null}
                </AnimatePresence>
              </motion.div>
            )
          })}
        </div>
      </div>
    </section>
  )
})